/* @flow */
/**
 * Redux Toolkit slice for collaboration PO report search criteria, saved searches,
 * results, exports, and pagination.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import {
  updateDialogStates,
  toggleSpinner, togglePaginationSpinner
} from '../dialogStates/dialogStatesSlice';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import { poReportsHeader } from '../../constants/ppmReportsHeader';
import {
  mapInputFieldsToRequest, mapRequestToInputFields, checkForColumnData, setValidFields
} from '../../helpers/dataHelpers';
import { poReportFieldList, initialReportFieldsState } from '../../constants/staticData';
import appConfig from '../../appConfig';
import collabReportFieldProperties from '../../constants/fieldProperties/collabReportFieldProperties';

export const name = 'poReport';

// Set lookup keeps export field filtering consistent with app configuration.
const excludedPOReportExportFields = new Set(appConfig.excludedPOReportFieldsInExportedFile);

function createInitialState() {
  return {
    collabReportResultData: {},
    collabReportSearchId: '',
    collabReportTotalCount: 0,
    collabReportNextResults: {},
    collabReportPrevResults: {},
    page: 0,
    rowsPerPage: 100,

    isSavedSearchModified: false,
    collabReportRequestBody: null,
    collabReportInputFieldData: { ...initialReportFieldsState },
    collabReportSelectedFields: [],
    collabReportBookmarkName: '',
    collabReportBookmarkId: '',
    requestError: false,
    jumpToPage: 0,
    apiCallAfterAppend: false,
    apiCallBeforeAppend: false
  };
}

export const initialState = createInitialState();

/**
 * Updates the collaboration report page index.
 */
function updateCollabReportPage(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_PAGE
  return {
    ...state,
    page: action.payload?.page ? action.payload?.page : action.payload
  };
}

/**
 * Updates the collaboration report page size.
 */
function updateCollabReportRowsPerPage(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_ROWS_PER_PAGE
  return {
    ...state,
    rowsPerPage: action.payload?.rowsPerPage ? action.payload?.rowsPerPage : action.payload
  };
}

/**
 * Stores the collaboration report request body and marks saved searches as
 * modified when it changes.
 */
function updateCollabReportRequestBody(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_REQUEST_BODY
  const { collabReportRequestBody } = action.payload;
  // Compare serialized request bodies to detect saved-search edits.
  const requestExist = JSON.stringify(state.collabReportRequestBody);
  return {
    ...state,
    collabReportRequestBody,
    isSavedSearchModified: Boolean(
      state.collabReportBookmarkId && requestExist !== JSON.stringify(collabReportRequestBody)
    )
  };
}

/**
 * Sets whether the active collaboration report saved search has unsaved changes.
 */
function updateIsSavedSearchModified(state: Object, action: Object) {
  return {
    ...state,
    isSavedSearchModified: Boolean(action.payload)
  };
}

/**
 * Replaces the collaboration report request body without changing bookmark metadata.
 */
function updateCollabReportRequestBodyOnSameBookmark(state: Object, action: Object) {
  // UPDATE_COLLAB_SEARCH_REQUEST_ON_SAME_BOOKMARK
  return {
    ...state,
    collabReportRequestBody: action.payload
  };
}

/**
 * Clears collaboration report results and pagination state.
 */
function resetReportResults(state: Object) {
  // RESET_REPORT_RESULTS
  return {
    ...state,
    collabReportResultData: {},
    collabReportSearchId: '',
    collabReportTotalCount: 0,
    collabReportNextResults: {},
    collabReportPrevResults: {},
    page: 0,
    rowsPerPage: 100
  };
}

/**
 * Resets collaboration report search criteria and saved-search metadata.
 */
function resetReportRequest() {
  // RESET_REPORT_REQUEST
  return {
    isSavedSearchModified: false,
    collabReportRequestBody: null,
    collabReportInputFieldData: { ...initialReportFieldsState },
    collabReportSelectedFields: [],
    collabReportBookmarkName: '',
    collabReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Clears collaboration report search criteria while preserving selected field uniqueness.
 */
function resetPOReportRequestForClearSearch(state: Object) {
  // RESET_PO_REPORT_REQUEST_DATA_CLEAR_SEARCH
  return {
    ...state,
    isSavedSearchModified: false,
    collabReportRequestBody: null,
    collabReportInputFieldData: { ...initialReportFieldsState },
    collabReportSelectedFields: [...new Set(state.collabReportSelectedFields)],
    collabReportBookmarkName: '',
    collabReportBookmarkId: '',
    requestError: false
  };
}

/**
 * Stores selected collaboration report field keys.
 */
function updateCollabReportFields(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_SELETED_FIELDS
  return {
    ...state, collabReportSelectedFields: action.payload
  };
}

/**
 * Stores collaboration report input field values.
 */
function updateCollabReportInputValues(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_INPUT_VALUES
  return { ...state, collabReportInputFieldData: action.payload };
}

/**
 * Stores the active collaboration report saved-search ID.
 */
function updateCollabBookmarkId(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_BOOKMARK_ID
  return { ...state, isSavedSearchModified: false, collabReportBookmarkId: action.payload };
}

/**
 * Clears active collaboration report saved-search metadata.
 */
function clearSavedCollabReport(state: Object) {
  // CLEAR_COLLAB_REPORT_BOOKMARK
  return {
    ...state,
    isSavedSearchModified: false,
    collabReportBookmarkId: '',
    collabReportBookmarkName: ''
  };
}

/**
 * Stores the active collaboration report saved-search name.
 */
function updateCollabBookmarkName(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_BOOKMARK_NAME
  return { ...state, isSavedSearchModified: false, collabReportBookmarkName: action.payload };
}

/**
 * Stores flattened collaboration report results and resets adjacent result buffers.
 */
function updateCollabReportResults(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_RESULTS
  const payload = {
    // Flatten nested API objects into the grid-friendly result shape.
    data: getflattenResponse(action.payload.data),
    dataNext: null
  };

  return {
    ...state,
    collabReportResultData: payload.data,
    collabReportTotalCount: payload.data.pages.totalResources,
    collabReportSearchId: cryptoRand().toString().slice(2, 10),
    selectedRecordsCount: 0,
    collabReportNextResults: null,
    collabReportPrevResults: null
  };
}

/**
 * Refreshes the collaboration report search identifier used to trigger UI updates.
 */
function updateCollabReportSearchId(state: Object) {
  // UPDATE_COLLAB_REPORT_SEARCH_ID
  return { ...state, collabReportSearchId: cryptoRand().toString().slice(2, 10) };
}

/**
 * Restores collaboration report input fields to their initial values.
 */
function resetCollabReportInputData(state: Object) {
  // RESET_COLLAB_REPORT_INPUT_VALUES
  return {
    ...state,
    collabReportInputFieldData: { ...initialReportFieldsState }
  };
}

/**
 * Stores the current and prefetched next collaboration report result sets.
 */
function updateCollabReportNextResult(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_NEXT_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataNext: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    collabReportResultData: payload.data,
    collabReportNextResults: payload.dataNext,
    collabReportTotalPages: payload.data.pages.totalPages,
    collabReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Stores the current and prefetched previous collaboration report result sets.
 */
function updateCollabReportPrevResult(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_PREV_RESULTS
  const payload = {
    data: getflattenResponse(action.payload.currentResultSet),
    dataPrev: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    collabReportResultData: payload.data,
    collabReportPrevResults: payload.dataPrev,
    collabReportTotalPages: payload.data.pages.totalPages,
    collabReportTotalCount: payload.data.pages.totalResources
  };
}

/**
 * Stores collaboration report results fetched from the first result offset.
 */
function PoReportOffset(state: Object, action: Object) {
  // PO_REPORT_RESULT_OFFSET
  const payload = {
    data: getflattenResponse(action.payload.response.data)
  };

  return {
    ...state,
    collabReportResultData: payload.data,
    collabReportTotalCount: payload.data.pages.totalResources,
    collabReportSearchId: cryptoRand().toString().slice(2, 10),
    selectedRecordsCount: 0,
    collabReportNextResults: null,
    collabReportPrevResults: null
  };
}

/**
 * Promotes the prefetched next collaboration report result set into the current page window.
 */
function appendNextResultSet(state: Object) {
  // UPDATE_COLLAB_REPORT_NEXT_APPEND
  // Preserve existing deep-copy behavior before swapping paged result buffers.
  const statecheck = JSON.parse(JSON.stringify(state));
  const updatedCurrentResultSet = statecheck.collabReportResultData;
  const updatedNextResultSet = statecheck.collabReportNextResults;

  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  return {
    ...statecheck,
    collabReportResultData: payload.data,
    collabReportPrevResults: payload.dataPrev,
    collabReportNextResults: null
  };
}

/**
 * Calculates whether jump-to-page navigation needs adjacent page-window API calls.
 */
function getJumpToPageApiCallFlags(page: number, rowPerPage: number) {
  // Page-window boundary checks depend on the visible page index and page size.
  const numberString = page?.toString();

  let checkApiCallApend = false;
  let checkApiCallPrepend = false;

  if (Number(rowPerPage) === 100) {
    checkApiCallApend = numberString?.endsWith('9') ?? false;
    checkApiCallPrepend = numberString?.endsWith('0') ?? false;
  } else if (Number(rowPerPage) === 250) {
    checkApiCallApend = (Number(numberString) + 1) % 4 === 0;
    checkApiCallPrepend = Number(numberString) % 4 === 0;
  } else if (Number(rowPerPage) === 500) {
    checkApiCallApend = (Number(numberString) + 1) % 2 === 0;
    checkApiCallPrepend = Number(numberString) % 2 === 0;
  }

  return { checkApiCallApend, checkApiCallPrepend };
}

/**
 * Promotes next jump-to-page results and updates prefetch flags for page-window boundaries.
 */
function appendJumpToPageNextResultSet(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_NEXT_APPEND
  const updatedCurrentResultSet = state.collabReportResultData;
  const updatedNextResultSet = state.collabReportNextResults;
  const { checkApiCallApend, checkApiCallPrepend } = getJumpToPageApiCallFlags(
    state.page,
    action.payload
  );

  const payload = {
    dataPrev: updatedCurrentResultSet,
    data: updatedNextResultSet
  };

  return {
    ...state,
    collabReportResultData: payload.data,
    collabReportPrevResults: payload.dataPrev,
    collabReportNextResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Promotes the prefetched previous collaboration report result set into the current page window.
 */
function appendPrevResultSet(state: Object) {
  // UPDATE_COLLAB_REPORT_PREV_APPEND
  const updatedPrevResultSet = state.collabReportPrevResults;
  const updatedCurrentResultSet = state.collabReportResultData;

  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  return {
    ...state,
    collabReportResultData: payload.data,
    collabReportNextResults: payload.dataNext,
    collabReportPrevResults: null
  };
}

/**
 * Promotes previous jump-to-page results and updates prefetch flags for page-window boundaries.
 */
function appendJumpToPagePrevResultSet(state: Object, action: Object) {
  // UPDATE_COLLAB_REPORT_PREV_APPEND
  const updatedPrevResultSet = state.collabReportPrevResults;
  const updatedCurrentResultSet = state.collabReportResultData;
  const { checkApiCallApend, checkApiCallPrepend } = getJumpToPageApiCallFlags(
    state.page,
    action.payload
  );

  const payload = {
    data: updatedPrevResultSet,
    dataNext: updatedCurrentResultSet
  };

  return {
    ...state,
    collabReportResultData: payload.data,
    collabReportNextResults: payload.dataNext,
    collabReportPrevResults: null,
    apiCallAfterAppend: checkApiCallApend,
    apiCallBeforeAppend: checkApiCallPrepend
  };
}

/**
 * Clears the prefetched previous collaboration report result set.
 */
function updatePreviousResultSettoNull(state: Object) {
  return {
    ...state,
    collabReportPrevResults: null
  };
}

/**
 * Clears the prefetched next collaboration report result set.
 */
function updateNextResultSettoNull(state: Object) {
  return {
    ...state,
    collabReportNextResults: null
  };
}

/**
 * Stores a requested jump-to-page index and marks the jump trigger.
 */
function updateJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    jumpToPage: action.payload,
    triggerJumpToPage: true
  };
}

/**
 * Updates whether pagination is processing a jump-to-page request.
 */
function updateTriggerJumpToPage(state: Object, action: Object) {
  return {
    ...state,
    triggerJumpToPage: action.payload
  };
}

/**
 * Updates whether another next-page API call is needed after appending results.
 */
function updateApiCallAfterAppend(state: Object, action: Object) {
  return {
    ...state,
    apiCallAfterAppend: action.payload
  };
}

/**
 * Updates whether another previous-page API call is needed after prepending results.
 */
function updateApiCallBeforePrepend(state: Object, action: Object) {
  return {
    ...state,
    apiCallBeforeAppend: action.payload
  };
}

/**
 * Collects reducers that manage collaboration report state.
 */
function createReducers() {
  return {
    updateCollabReportPage,
    updateCollabReportRowsPerPage,
    updateCollabReportRequestBody,
    updateIsSavedSearchModified,
    updateCollabReportRequestBodyOnSameBookmark,
    resetReportResults,
    resetReportRequest,
    resetPOReportRequestForClearSearch,
    updateCollabReportFields,
    updateCollabReportInputValues,
    updateCollabBookmarkId,
    clearSavedCollabReport,
    updateCollabBookmarkName,
    updateCollabReportResults,
    updateCollabReportSearchId,
    resetCollabReportInputData,
    updateCollabReportNextResult,
    updateCollabReportPrevResult,
    PoReportOffset,
    appendNextResultSet,
    appendJumpToPageNextResultSet,
    appendPrevResultSet,
    appendJumpToPagePrevResultSet,
    updateJumpToPage,
    updateTriggerJumpToPage,
    updateApiCallAfterAppend,
    updateApiCallBeforePrepend,
    updatePreviousResultSettoNull,
    updateNextResultSettoNull
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Creates a thunk that searches collaboration reports using collaborationReportSearch.
 */
function fetchPOReports() {
  return createAsyncThunk(
    `${name}/fetchPOReports`,
    async ({
      data,
      callback,
      modifiedrequestBody = {},
      snapshot = false
    }, { getState, dispatch }) => {
      dispatch(toggleSpinner(true));
      const secondaryFields = [];
      const fields = poReportsHeader
        .filter((header) => header.default)
        .map((header) => {
          if (header.secondary) {
            secondaryFields.push(header.secondary);
          }
          return header.primary;
        });
      const state = getState();
      let requestData = {};
      const {
        selectedColumnOrderOptions: columnOrderOptions,
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};

      // Prefer user-selected column order; otherwise request default headers and secondary fields.
      const fieldsForRequest = checkForColumnData(state, true)
        ? columnOrderOptions
        : [...fields, ...secondaryFields];
      if (data) {
        requestData = mapInputFieldsToRequest(data, 'collabReport');
        const appliedSearchReportField = new Set(
          requestData.map((field) => field.fieldName)
        );
        dispatch(actions.updateCollabReportInputValues(data));
        dispatch(actions.updateCollabReportFields(poReportFieldList
          .map((field) => field.key)
          .filter((field) => appliedSearchReportField.has(field))));
      } else {
        requestData = state.poReport.collabReportRequestBody.search;
      }

      // Snapshot searches use the user-restricted report search type.
      const requestSearchType = snapshot ? 'COLLAB_REPORT_RESTRICT_USERID' : 'COLLAB_REPORT';
      const isSavedSearchModifiedCheck = !snapshot;
      let requestBody = (state.poReport.collabReportRequestBody)
        ? {
          ...state.poReport.collabReportRequestBody,
          search: requestData,
          searchType: requestSearchType,
          offset: '0',
          ...modifiedrequestBody
        }
        : {
          fields: fieldsForRequest,
          search: requestData,
          filter: [],
          count: appConfig.SearchThresholdLimit,
          offset: '0',
          searchType: requestSearchType,
          ...modifiedrequestBody
        };
      requestBody = setValidFields(collabReportFieldProperties, requestBody);
      // Hidden fields are included for UI needs but kept out of saved request state.
      const requestFieldSet = new Set(requestBody.fields);
      const requestBodyWithHiddenFields = {
        ...requestBody,
        fields: [
          ...requestBody.fields,
          ...appConfig.includeFieldsCollabReport.filter(
            (item) => !requestFieldSet.has(item)
          )
        ]
      };

      request({
        api: 'collaborationReportSearch',
        method: 'post',
        data: requestBodyWithHiddenFields,
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          dispatch(
            actions.updateCollabReportRequestBody({
              collabReportRequestBody: requestBody,
              isSavedSearchModified: isSavedSearchModifiedCheck
            })
          );
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(response.data);
          }
          dispatch(actions.updateCollabReportResults(response));
          dispatch(actions.updateCollabReportRowsPerPage(rowsPerPageOption));
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
          console.log(error);
        });
    }
  );
}

/**
 * Creates a thunk that exports the current collaboration report through
 * collaborationReportExportLargeFile.
 */
function exportPOReport() {
  return createAsyncThunk(
    `${name}/exportPOReport`,
    async ({
      callback,
      filetype
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(toggleSpinner(true));
      const searchRequestBody = {
        ...state.poReport.collabReportRequestBody
      };
      const requestBody = {
        search: [...(searchRequestBody.search) || []],
        fields: [...(searchRequestBody.fields) || []].filter((field: string) => (
          // Export omits fields configured as hidden from downloaded files.
          !excludedPOReportExportFields.has(field)
        )),
        filter: [...(searchRequestBody.filter) || []],
        searchType: searchRequestBody.searchType,
        fileFormat: filetype
      };
      request({
        api: 'collaborationReportExportLargeFile',
        method: 'post',
        data: requestBody
      }, dispatch, getState)
        .then((response) => {
          if (callback) {
            callback(response);
          }
          dispatch(toggleSpinner(false));
        })
        .catch((error) => {
          if (callback) {
            callback(null, error);
          }
          dispatch(toggleSpinner(false));
        });
    }
  );
}

/**
 * Creates a thunk that executes a saved collaboration report search by ID.
 */
function fetchSavedCollabReportResults() {
  return createAsyncThunk(
    `${name}/fetchSavedCollabReportResults`,
    async ({
      collabReportRequestData,
      callback
    }, { getState, dispatch }) => {
      const state = getState();
      // Saved searches always execute from the first result window.
      let requestBody = { ...collabReportRequestData.searchCriteria, offset: '0' };
      requestBody = setValidFields(collabReportFieldProperties, requestBody);
      dispatch(toggleSpinner(true));
      dispatch(updateDialogStates({ executeBookmarkSpinner: true }));
      const {
        rowPerPageOption: rowsPerPageOption
      } = state.searchpaneldata || {};
      const savedRequestFieldSet = new Set(requestBody.fields);
      const requestBodyWithHiddenFields = {
        ...requestBody,
        fields: [
          ...requestBody.fields,
          ...appConfig.includeFieldsCollabReport.filter(
            (item) => !savedRequestFieldSet.has(item)
          )
        ]
      };
      request({
        api: 'collaborationReportSearch',
        method: 'post',
        data: {
          savedSearchID: collabReportRequestData.id,
          ...requestBodyWithHiddenFields
        },
        cancellable: true
      }, dispatch, getState)
        .then((response) => {
          const fields = mapRequestToInputFields([
            ...collabReportRequestData.searchCriteria.search
          ]);
          const requestedSearchReportField = new Set(
            collabReportRequestData.searchCriteria.search.map((field) => field.fieldName)
          );
          dispatch(actions.updateCollabReportInputValues(
            fields
          ));
          dispatch(actions.updateCollabReportRequestBody({
            collabReportRequestBody: requestBody
          }));
          dispatch(actions.updateCollabReportFields(poReportFieldList
            .map((field) => field.key)
            .filter((field) => requestedSearchReportField.has(field))));
          dispatch(actions.updateCollabReportResults(response));
          dispatch(actions.updateCollabReportSearchId());
          dispatch(actions.updateCollabReportPage(0));
          dispatch(actions.updateCollabReportRowsPerPage(rowsPerPageOption));
          dispatch(actions.updateCollabBookmarkId(collabReportRequestData.id));
          dispatch(actions.updateCollabBookmarkName(collabReportRequestData.name));
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          if (callback) {
            callback(response);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          dispatch(updateDialogStates({ executeBookmarkSpinner: false }));
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Creates a thunk that reloads collaboration report results from offset zero.
 */
function fetchPOreportsResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchPOreportsResultsOffset`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.poReport.collabReportRequestBody
          && state.poReport.collabReportResultData) {
        let updatedRequest = state.poReport.collabReportRequestBody;
        updatedRequest = { ...updatedRequest, offset: '0' };

        dispatch(toggleSpinner(true));
        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.PoReportOffset({ response }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateCollabReportSearchId());
          })
          .catch((error) => {
            console.log('NEXT Error ', error);
            dispatch(toggleSpinner(false));
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper for resetting collaboration report data to the first offset.
 */
function PoReportDataOffset() {
  return createAsyncThunk(
    `${name}/PoReportDataOffset`,
    async ({
      poFetchPOreportsResultsOffset
    }, { dispatch }) => {
      dispatch(poFetchPOreportsResultsOffset());
    }
  );
}

/**
 * Creates a thunk that prefetches next collaboration report results for jump-to-page navigation.
 */
function fetchReportJumpToPageNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportJumpToPageNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallAfterAppend(false));
      if (state.poReport.collabReportRequestBody
          && state.poReport.collabReportResultData?.pages?.next) {
        const currentResultSet = state.poReport.collabReportResultData;
        let updatedRequest = state.poReport.collabReportRequestBody;
        const searchResultNextOffset = state.poReport.collabReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          // API cursor text contains the numeric offset before the first comma.
          offset: searchResultNextOffset.slice(7, resNext)
        };

        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updateCollabReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateCollabReportNextResult({ response, currentResultSet }));
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk that loads the next collaboration report result window.
 */
function fetchReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.poReport.collabReportRequestBody
          && state.poReport.collabReportResultData?.pages?.next) {
        const currentResultSet = state.poReport.collabReportResultData;
        const currentJumpToPage = state.poReport.jumpToPage || 0;
        // Results are fetched in 1000-row windows, so jump pages map to window boundaries.
        const pageGroupSize = 1000 / state.poReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.poReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }
        let updatedRequest = state.poReport.collabReportRequestBody;
        const searchResultNextOffset = state.poReport.collabReportResultData.pages.next;
        const resNext = searchResultNextOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            // Jump navigation starts at the computed window; regular paging follows the API cursor.
            ? (pageGroup * 1000).toString() : searchResultNextOffset.slice(7, resNext)
        };

        dispatch(togglePaginationSpinner({ isFetchingNextData: true }));
        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updateCollabReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateCollabReportNextResult({ response, currentResultSet }));
            if (triggerJumpToPage) {
              dispatch(actions.appendJumpToPageNextResultSet(state.poReport.rowsPerPage));
              dispatch(actions.updatePreviousResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('NEXT Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper for loading the next collaboration report result window.
 */
function reportsDataNextSet() {
  return createAsyncThunk(
    `${name}/reportsDataNextSet`,
    async ({
      poFetchReportNextResultSet
    }, { dispatch }) => {
      dispatch(poFetchReportNextResultSet());
    }
  );
}

/**
 * Creates a thunk that prefetches previous collaboration report results for
 * jump-to-page navigation.
 */
function fetchReportJumpToPagePrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportJumpToPagePrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.updateApiCallBeforePrepend(false));
      if (state.poReport.collabReportRequestBody
          && state.poReport.collabReportResultData?.pages?.prev) {
        const currentResultSet = state.poReport.collabReportResultData;
        let updatedRequest = state.poReport.collabReportRequestBody;
        const searchResultPrevOffset = state.poReport.collabReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: searchResultPrevOffset.slice(7, res)
        };

        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updateCollabReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateCollabReportPrevResult({ response, currentResultSet }));
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('PREV Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk that loads the previous collaboration report result window.
 */
function fetchReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportPrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.poReport.collabReportRequestBody
          && state.poReport.collabReportResultData?.pages?.prev) {
        const currentResultSet = state.poReport.collabReportResultData;
        const currentJumpToPage = state.poReport.jumpToPage || 0;
        const pageGroupSize = 1000 / state.poReport.rowsPerPage;
        const pageGroup = currentJumpToPage > 0
          ? Math.floor(currentJumpToPage / pageGroupSize) : 0;
        const { triggerJumpToPage } = state.poReport;
        if (triggerJumpToPage) {
          dispatch(actions.updateTriggerJumpToPage(false));
        }

        let updatedRequest = state.poReport.collabReportRequestBody;
        const searchResultPrevOffset = state.poReport.collabReportResultData.pages.prev;
        const res = searchResultPrevOffset.indexOf(',');
        updatedRequest = {
          ...updatedRequest,
          offset: triggerJumpToPage
            // Jump navigation starts at the computed window; regular paging follows the API cursor.
            ? (pageGroup * 1000).toString() : searchResultPrevOffset.slice(7, res)
        };

        dispatch(togglePaginationSpinner({ isFetchingPrevData: true }));
        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState)
          .then((response) => {
            dispatch(actions.updateCollabReportRequestBodyOnSameBookmark(updatedRequest));
            dispatch(togglePaginationSpinner({ isFetchingPrevData: false }));
            dispatch(toggleSpinner(false));
            dispatch(actions.updateCollabReportPrevResult({ response, currentResultSet }));
            if (triggerJumpToPage) {
              dispatch(actions.appendJumpToPagePrevResultSet(state.poReport.rowsPerPage));
              dispatch(actions.updateNextResultSettoNull());
            }
          })
          .catch((error) => {
            dispatch(togglePaginationSpinner({ isFetchingNextData: false }));
            console.log('PREV Error ', error);
          });
      }
    }
  );
}

/**
 * Creates a thunk wrapper for loading the previous collaboration report result window.
 */
function reportsDataPrevSet() {
  return createAsyncThunk(
    `${name}/reportsDataPrevSet`,
    async ({
      poFetchReportPrevResultSet
    }, { dispatch }) => {
      dispatch(poFetchReportPrevResultSet());
    }
  );
}

/**
 * Instantiates async collaboration report actions exposed by the slice.
 */
function createExtraActions() {
  return {
    fetchPOReports: fetchPOReports(),
    exportPOReport: exportPOReport(),
    fetchSavedCollabReportResults: fetchSavedCollabReportResults(),
    fetchPOreportsResultsOffset: fetchPOreportsResultsOffset(),
    PoReportDataOffset: PoReportDataOffset(),
    fetchReportNextResultSet: fetchReportNextResultSet(),
    reportsDataNextSet: reportsDataNextSet(),
    fetchReportPrevResultSet: fetchReportPrevResultSet(),
    reportsDataPrevSet: reportsDataPrevSet(),
    fetchReportJumpToPageNextResultSet: fetchReportJumpToPageNextResultSet(),
    fetchReportJumpToPagePrevResultSet: fetchReportJumpToPagePrevResultSet()
  };
}

export const extraActions = createExtraActions();

export const poReportActions = { ...actions, ...extraActions };
export const poReportReducer = slice.reducer;
